import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft, Mail, Phone, Building2, AlertCircle, FileText, MessageSquare, Plus, Loader2, CheckCircle, Receipt, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtAUD = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export default function AccountDetails() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [statements, setStatements] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [emailingId, setEmailingId] = useState(null);

  const downloadStatement = async (s) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/accounts/statements/${s.statement_id}/pdf`, { credentials: 'include' });
      if (!r.ok) { toast.error('Could not download statement PDF'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${s.doc_no || 'Statement'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const emailStatement = async (s) => {
    if (!window.confirm(`Email statement ${s.doc_no} with all invoice PDFs attached to the account holder?`)) return;
    setEmailingId(s.statement_id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/accounts/statements/${s.statement_id}/email`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Statement emailed to ${data.sent_to} — ${data.invoices_attached} invoice PDF(s) attached${data.invoices_skipped?.length ? `, ${data.invoices_skipped.length} skipped (too large)` : ''}`);
        refresh();
      } else {
        toast.error(data.detail || 'Failed to email statement');
      }
    } catch (e) {
      toast.error(`Failed to email statement: ${e.message}`);
    } finally {
      setEmailingId(null);
    }
  };

  useEffect(() => {
    (async () => {
      const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (!r.ok) { navigate('/login'); return; }
      const u = await r.json();
      if (u.role !== 'super_admin') { navigate('/'); return; }
      setUser(u);
    })();
  }, [navigate]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, i, s, f] = await Promise.all([
        fetch(`${BACKEND_URL}/api/accounts/${accountId}`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND_URL}/api/accounts/${accountId}/invoices`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND_URL}/api/accounts/${accountId}/statements`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BACKEND_URL}/api/accounts/${accountId}/followups`, { credentials: 'include' }).then((r) => r.json()),
      ]);
      setAccount(a); setInvoices(i || []); setStatements(s || []); setFollowups(f || []);
    } catch (_e) {
      toast.error('Failed to load account');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user) refresh();   }, [user, accountId]);

  if (!user) return null;
  if (loading || !account) return (
    <Layout user={user}><div className="p-8"><Loader2 className="h-6 w-6 animate-spin" /></div></Layout>
  );

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="account-details-page">
        <Button variant="ghost" onClick={() => navigate('/accounts')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Accounts
        </Button>

        {/* Hero */}
        <Card className="p-6 mb-6 bg-gradient-to-br from-slate-900 to-slate-700 text-slate-50 border-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Building2 className="h-10 w-10 text-slate-400" />
              <div>
                <h1 className="text-2xl font-semibold">{account.account_name}</h1>
                <p className="text-slate-300 text-sm">
                  {account.account_code && <span className="font-mono mr-3">{account.account_code}</span>}
                  Customer: {account.customer?.display_name || '—'}
                </p>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-300">
                  {account.contact_email && (<span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{account.contact_email}</span>)}
                  {account.contact_phone && (<span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{account.contact_phone}</span>)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="bg-white/10 px-3 py-2 rounded-lg">
                <p className="text-xs text-slate-300 uppercase">Status</p>
                <p className="font-semibold capitalize">{account.status}</p>
              </div>
              <div className="bg-white/10 px-3 py-2 rounded-lg">
                <p className="text-xs text-slate-300 uppercase">Credit terms</p>
                <p className="font-semibold">Net {account.credit_terms_days} days</p>
              </div>
              <div className="bg-white/10 px-3 py-2 rounded-lg">
                <p className="text-xs text-slate-300 uppercase">Credit limit</p>
                <p className="font-semibold">{account.credit_limit ? fmtAUD(account.credit_limit) : '—'}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* KPI row — AR aging snapshot */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="account-aging-tiles">
          <KpiCard
            label="Total Due (Current Balance)"
            value={fmtAUD(account.current_balance)}
            tone="blue"
            data-testid="kpi-current-balance"
          />
          <KpiCard
            label="Last Month Pending Due (0–30 days)"
            sublabel="1–30 days past due"
            value={fmtAUD(account.aging?.aging_0_30 || 0)}
            tone={(account.aging?.aging_0_30 || 0) > 0 ? 'amber' : 'slate'}
            data-testid="kpi-aging-0-30"
          />
          <KpiCard
            label="60 Days Pending"
            sublabel="31–60 days past due"
            value={fmtAUD(account.aging?.aging_31_60 || 0)}
            tone={(account.aging?.aging_31_60 || 0) > 0 ? 'orange' : 'slate'}
            data-testid="kpi-aging-31-60"
          />
          <KpiCard
            label="Total Overdue"
            sublabel="All overdue invoices"
            value={fmtAUD(account.overdue_amount)}
            tone={account.overdue_amount > 0 ? 'rose' : 'slate'}
            data-testid="kpi-total-overdue"
          />
        </div>

        {/* Secondary tile row — informational */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="account-meta-tiles">
          <KpiCard
            label="Not Yet Due"
            sublabel="Future / today"
            value={fmtAUD(account.aging?.not_yet_due || 0)}
            tone="emerald"
            data-testid="kpi-not-yet-due"
          />
          <KpiCard
            label="60+ Days Overdue"
            sublabel="Long-overdue"
            value={fmtAUD(account.aging?.aging_60_plus || 0)}
            tone={(account.aging?.aging_60_plus || 0) > 0 ? 'rose' : 'slate'}
            data-testid="kpi-aging-60-plus"
          />
          <KpiCard
            label="Open Invoices"
            value={account.open_invoices_count}
            data-testid="kpi-open-invoices"
          />
          <KpiCard
            label="Last Invoice"
            value={account.last_invoice_date ? new Date(account.last_invoice_date).toLocaleDateString('en-AU') : '—'}
            data-testid="kpi-last-invoice"
          />
        </div>

        <Tabs defaultValue="invoices">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="invoices" data-testid="tab-acct-invoices"><FileText className="h-4 w-4 mr-1" /> Invoices</TabsTrigger>
            <TabsTrigger value="statements" data-testid="tab-acct-statements"><Receipt className="h-4 w-4 mr-1" /> Statements</TabsTrigger>
            <TabsTrigger value="followups" data-testid="tab-acct-followups"><MessageSquare className="h-4 w-4 mr-1" /> Follow-ups</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="mt-4">
            <Card className="p-4">
              <h3 className="font-medium mb-3">All invoices on this account ({invoices.length})</h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-center">Statement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-6">No invoices yet on this account.</TableCell></TableRow>
                  ) : invoices.map((inv) => (
                    <TableRow key={inv.invoice_id} className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-1.5">
                          <span>{inv.doc_no}</span>
                          {/* iter138.9af — flag invoices visible via the
                              customer_id fallback so SA knows to click
                              "Sync orphan invoices" to link them
                              properly for month-end statements. */}
                          {inv.account_linked === false && (
                            <Badge
                              className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0"
                              title="This invoice is not yet linked to the credit account. Click 'Sync orphan invoices' on the Accounts page to stamp it."
                              data-testid={`account-invoice-orphan-${inv.invoice_id}`}
                            >
                              UNLINKED
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-AU') : '—'}</TableCell>
                      <TableCell className={inv.balance > 0 && inv.due_date && new Date(inv.due_date) < new Date() ? 'text-rose-600 font-medium' : ''}>
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-AU') : '—'}
                      </TableCell>
                      <TableCell>{[inv.vehicle_rego, inv.vehicle_make, inv.vehicle_model].filter(Boolean).join(' ')}</TableCell>
                      <TableCell className="text-right">{fmtAUD(inv.total)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{fmtAUD(inv.amount_paid)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtAUD(inv.balance)}</TableCell>
                      <TableCell className="text-center text-xs font-mono text-slate-500">{inv.statement_id ? '✓' : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="statements" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Monthly Statements</h3>
              <Button onClick={() => setShowGenerate(true)} size="sm" data-testid="generate-statement-btn">
                <Plus className="h-4 w-4 mr-2" /> Generate Statement
              </Button>
            </div>
            {statements.length === 0 ? (
              <Card className="p-8 text-center text-slate-500">No statements generated yet.</Card>
            ) : statements.map((s) => (
              <Card key={s.statement_id} className="p-4" data-testid="statement-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="font-semibold">{s.doc_no}</p>
                    <p className="text-sm text-slate-500">
                      Period {new Date(s.period_start).toLocaleDateString('en-AU')} → {new Date(s.period_end).toLocaleDateString('en-AU')} ·
                      Due {new Date(s.due_date).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatementBadge status={s.status} />
                    <Button size="sm" variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      data-testid={`download-statement-btn-${s.statement_id}`}
                      title="Download this statement as PDF"
                      onClick={() => downloadStatement(s)}>
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      disabled={emailingId === s.statement_id}
                      data-testid={`email-statement-btn-${s.statement_id}`}
                      title="Email this statement + all invoice PDFs to the account holder"
                      onClick={() => emailStatement(s)}>
                      <Mail className="h-3.5 w-3.5 mr-1" />
                      {emailingId === s.statement_id ? 'Sending…' : 'Email'}
                    </Button>
                    {s.status === 'draft' && (
                      <Button size="sm" variant="outline"
                        onClick={async () => {
                          const r = await fetch(`${BACKEND_URL}/api/accounts/statements/${s.statement_id}/mark-sent`, { method: 'PUT', credentials: 'include' });
                          if (r.ok) { toast.success('Marked as sent'); refresh(); }
                        }}>
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Mark sent
                      </Button>
                    )}
                  </div>
                </div>
                {s.sent_to && (
                  <p className="text-xs text-slate-400 mb-2" data-testid="statement-sent-to">
                    Emailed to {s.sent_to}{s.sent_at ? ` · ${new Date(s.sent_at).toLocaleString('en-AU')}` : ''}
                  </p>
                )}
                {Number(s.reminder_count) > 0 && (
                  <p className="text-xs text-amber-600 mb-2" data-testid="statement-reminder-info">
                    {s.reminder_count} overdue reminder{s.reminder_count > 1 ? 's' : ''} sent
                    {s.last_reminder_at ? ` · last ${new Date(s.last_reminder_at).toLocaleDateString('en-AU')}` : ''}
                  </p>
                )}
                <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                  <div><span className="text-slate-500">Total:</span> <span className="font-semibold">{fmtAUD(s.total_amount)}</span></div>
                  <div><span className="text-slate-500">Paid:</span> <span className="font-semibold text-emerald-600">{fmtAUD(s.paid_amount)}</span></div>
                  <div><span className="text-slate-500">Balance:</span> <span className="font-semibold text-rose-600">{fmtAUD(s.balance_due)}</span></div>
                </div>
                <details>
                  <summary className="text-sm text-blue-600 cursor-pointer">View {s.lines.length} invoice line(s)</summary>
                  <Table className="mt-2">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Invoice</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {s.lines.map((l) => (
                        <TableRow key={l.invoice_id}>
                          <TableCell className="font-mono text-xs">{l.doc_no}</TableCell>
                          <TableCell>{l.invoice_date}</TableCell>
                          <TableCell>{l.description}</TableCell>
                          <TableCell className="text-right">{fmtAUD(l.total)}</TableCell>
                          <TableCell className="text-right">{fmtAUD(l.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </details>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="followups" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Follow-up log</h3>
              <Button onClick={() => setShowFollowup(true)} size="sm" data-testid="add-followup-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Follow-up
              </Button>
            </div>
            {followups.length === 0 ? (
              <Card className="p-8 text-center text-slate-500">No follow-up notes yet.</Card>
            ) : followups.map((f) => (
              <Card key={f.followup_id} className="p-4">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className="capitalize">{f.type.replace('_', ' ')}</Badge>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{f.notes}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(f.created_at).toLocaleString('en-AU')}
                      {f.scheduled_for && <span> · Scheduled {new Date(f.scheduled_for).toLocaleString('en-AU')}</span>}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>

        <GenerateStatementDialog
          open={showGenerate}
          onClose={() => setShowGenerate(false)}
          accountId={accountId}
          onCreated={refresh}
        />
        <FollowupDialog
          open={showFollowup}
          onClose={() => setShowFollowup(false)}
          accountId={accountId}
          onCreated={refresh}
        />
      </div>
    </Layout>
  );
}

function KpiCard({ label, sublabel, value, tone = 'slate', 'data-testid': dataTestId }) {
  const tones = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    blue: 'text-blue-600',
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    orange: 'text-orange-600',
  };
  return (
    <Card className="p-4" data-testid={dataTestId}>
      <p className="text-xs uppercase tracking-wide text-slate-500 leading-tight">{label}</p>
      {sublabel && <p className="text-[10px] text-slate-400 mt-0.5">{sublabel}</p>}
      <p className={`text-xl font-semibold mt-1 ${tones[tone] || tones.slate}`}>{value ?? '—'}</p>
    </Card>
  );
}

function StatementBadge({ status }) {
  const map = {
    draft: 'bg-slate-200 text-slate-700',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700',
    partial: 'bg-amber-100 text-amber-700',
    overdue: 'bg-rose-100 text-rose-700',
    void: 'bg-slate-200 text-slate-500',
  };
  return <Badge className={`${map[status] || ''} hover:${map[status] || ''}`}>{status}</Badge>;
}

function GenerateStatementDialog({ open, onClose, accountId, onCreated }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/accounts/statements/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, year, month }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const stmt = await r.json();
      toast.success(`Statement ${stmt.doc_no} generated — due ${new Date(stmt.due_date).toLocaleDateString('en-AU')}`);
      onCreated(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
  const months = [
    [1,'Jan'],[2,'Feb'],[3,'Mar'],[4,'Apr'],[5,'May'],[6,'Jun'],
    [7,'Jul'],[8,'Aug'],[9,'Sep'],[10,'Oct'],[11,'Nov'],[12,'Dec']
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Generate Monthly Statement</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Month</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{months.map(([n, l]) => <SelectItem key={n} value={String(n)}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded">
            <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
            Statement will close on the last day of the month and become due on the 10th of the following month. Re-running for the same month replaces the previous draft.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowupDialog({ open, onClose, accountId, onCreated }) {
  const [type, setType] = useState('note');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!notes.trim()) return toast.error('Notes required');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/followups`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, notes: notes.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success('Follow-up logged');
      setNotes(''); setType('note');
      onCreated(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Follow-up</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Internal note</SelectItem>
                <SelectItem value="call">Phone call</SelectItem>
                <SelectItem value="reminder_queued">Reminder queued (email/SMS)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="e.g. Called Frank, promised payment by Friday" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
